import React, { useEffect, useState } from 'react';
import { landlordAPI } from '../../../lib/api';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Separator } from '../../ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const EMPTY_FORM = {
  name: '', phone: '', email: '', notes: '', birthday: '',
  school_name: '', student_id: '',
  home_address: '', emergency_contact_name: '', emergency_contact_phone: '',
  emergency_contact_relationship: '', emergency_contact_address: '', property_id: '', unit_id: ''
};

export const EditTenantDialog = ({ open, onOpenChange, tenant, selectedType, onSaved }) => {
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => {
    if (open && tenant) {
      setEditForm({
        name: tenant.name || tenant.tenant_name || '',
        phone: tenant.phone || tenant.tenant_phone || '',
        email: tenant.email || tenant.tenant_email || '',
        notes: tenant.notes || '',
        birthday: tenant.birthday || '',
        school_name: tenant.school_name || '',
        student_id: tenant.student_id || '',
        home_address: tenant.home_address || '',
        emergency_contact_name: tenant.emergency_contact?.name || '',
        emergency_contact_phone: tenant.emergency_contact?.phone || '',
        emergency_contact_relationship: tenant.emergency_contact?.relationship || '',
        emergency_contact_address: tenant.emergency_contact?.address || '',
        property_id: tenant.property_id || '',
        unit_id: tenant.unit_id || ''
      });
    }
  }, [open, tenant]);

  const handleSaveEdit = async () => {
    setSavingEdit(true);
    try {
      const payload = {};
      Object.entries(editForm).forEach(([k, v]) => { if (v !== '') payload[k] = v; });
      payload.name = editForm.name;

      const tenantId = tenant?.user_id || tenant?.pending_tenant_id || '';
      if (selectedType === 'manual') await landlordAPI.editManualTenant(tenantId, payload);
      else if (selectedType === 'invited') await landlordAPI.editPendingTenant(tenant.pending_tenant_id, payload);
      else await landlordAPI.editTenant(tenantId, payload);

      toast.success('Tenant updated');
      onOpenChange(false);
      onSaved(payload);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    } finally { setSavingEdit(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Tenant</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="edit-name" />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} data-testid="edit-phone" />
            </div>
          </div>
          {(selectedType === 'manual' || selectedType === 'invited') && (
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} data-testid="edit-email" />
            </div>
          )}
          <Separator />
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Date of Birth</Label>
              <Input type="date" value={editForm.birthday} onChange={(e) => setEditForm({ ...editForm, birthday: e.target.value })} data-testid="edit-birthday" />
            </div>
            <div className="space-y-2">
              <Label>School / University</Label>
              <Input value={editForm.school_name} onChange={(e) => setEditForm({ ...editForm, school_name: e.target.value })} placeholder="e.g., UP Diliman" data-testid="edit-school-name" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Student ID Number</Label>
            <Input value={editForm.student_id} onChange={(e) => setEditForm({ ...editForm, student_id: e.target.value })} placeholder="e.g., 2024-12345" data-testid="edit-student-id" />
          </div>
          <div className="space-y-2">
            <Label>Complete Address</Label>
            <Input value={editForm.home_address} onChange={(e) => setEditForm({ ...editForm, home_address: e.target.value })} placeholder="Street, City, Province, ZIP" data-testid="edit-home-address" />
          </div>
          <Separator />
          <p className="text-sm font-medium text-muted-foreground">Emergency Contact</p>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input value={editForm.emergency_contact_name} onChange={(e) => setEditForm({ ...editForm, emergency_contact_name: e.target.value })} data-testid="edit-emergency-name" />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input value={editForm.emergency_contact_phone} onChange={(e) => setEditForm({ ...editForm, emergency_contact_phone: e.target.value })} data-testid="edit-emergency-phone" />
            </div>
            <div className="space-y-2">
              <Label>Relationship</Label>
              <Input value={editForm.emergency_contact_relationship} onChange={(e) => setEditForm({ ...editForm, emergency_contact_relationship: e.target.value })} data-testid="edit-emergency-relationship" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Emergency Contact Address</Label>
            <Input value={editForm.emergency_contact_address} onChange={(e) => setEditForm({ ...editForm, emergency_contact_address: e.target.value })} placeholder="Street, City, Province, ZIP" data-testid="edit-emergency-address" />
          </div>
          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} rows={2} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={handleSaveEdit} disabled={savingEdit || !editForm.name} className="bg-[#C85A42] hover:bg-[#B54A35]" data-testid="save-edit-btn">
              {savingEdit ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Save
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};
